if (!customElements.get('wlsn-scrolling-bar')) {
  class WLSNScrollingBar extends HTMLElement {
    constructor() {
      super();

      const n = (v, fb = 40) => {
        const num = Number(v);
        return Number.isFinite(num) && num >= 0 ? num : fb;
      };
      this.scrollSpeedDesktop = n(this.getAttribute('data-speed-desk'), 40);
      this.scrollSpeedTablet = n(this.getAttribute('data-speed-tab'), 40);
      this.scrollSpeedMobile = n(this.getAttribute('data-speed-mob'), 40);

      this.allowAnimation = String(this.getAttribute('data-allow-animation')).toLowerCase() === 'true';
      this.pauseOnHoverDesktop = String(this.getAttribute('data-pause-on-hover-desk')).toLowerCase() === 'true';

      this.classes = { track: 'js-wlsn-scrolling-bar-track' };
      this.state = {
        rafId: 0,
        lastTs: null,
        x: 0,
        wrapWidth: 0,
        originalItemCount: 0,
        paused: false,
        built: false,
        triedAutoStart: false,
      };

      this._intersectionObserver = null;
      this._resizeObserver = null;
      this._mqlDesktop = window.matchMedia('(min-width: 1024px)');
      this._mqlTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023.98px)');
      this._mqlMobile = window.matchMedia('(max-width: 767.98px)');
      this._mqlReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

      this._onVisChange = this._onVisChange.bind(this);
      this._onMqlChange = this._onMqlChange.bind(this);
      this._onResize = this._debounce(() => this.rebuild(), 120);
      this._onIntersect = this._onIntersect.bind(this);
      this._autoStart = this._autoStart.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return { track: this.querySelector(`.${this.classes.track}`) };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (!this.allowAnimation) return;
      const { track } = this.elements;
      if (!track) return;

      track.style.willChange = 'transform';

      // Don't hang on images forever
      this._waitForImages(this, 1200).then(() => this._build());

      document.addEventListener('visibilitychange', this._onVisChange);
      this._mqlDesktop.addEventListener('change', this._onMqlChange);
      this._mqlTablet.addEventListener('change', this._onMqlChange);
      this._mqlMobile.addEventListener('change', this._onMqlChange);
      this._mqlReduced.addEventListener('change', this._onMqlChange);

      this._intersectionObserver = new IntersectionObserver(this._onIntersect, { root: null, threshold: 0 });
      this._intersectionObserver.observe(this);

      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this);

      window.addEventListener('resize', this._onResize, { passive: true });
      // Mobile-specific: handle orientation change
      window.addEventListener(
        'orientationchange',
        () => {
          setTimeout(() => this.rebuild(), 100);
        },
        { passive: true }
      );

      // Setup hover listeners for desktop
      if (this.pauseOnHoverDesktop) {
        this.addEventListener('mouseenter', this._onMouseEnter, { passive: true });
        this.addEventListener('mouseleave', this._onMouseLeave, { passive: true });
      }

      // Safety autostart: increased timeout for slower mobile devices
      setTimeout(this._autoStart, 1500);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._teardown();
      document.removeEventListener('visibilitychange', this._onVisChange);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._rebuild);

      this._mqlDesktop.removeEventListener('change', this._onMqlChange);
      this._mqlTablet.removeEventListener('change', this._onMqlChange);
      this._mqlMobile.removeEventListener('change', this._onMqlChange);
      this._mqlReduced.removeEventListener('change', this._onMqlChange);

      if (this.pauseOnHoverDesktop) {
        this.removeEventListener('mouseenter', this._onMouseEnter);
        this.removeEventListener('mouseleave', this._onMouseLeave);
      }

      if (this._intersectionObserver) {
        this._intersectionObserver.disconnect();
        this._intersectionObserver = null;
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    /**
     * Pause scrolling animation
     * @returns {void}
     */
    pause() {
      if (this.state.paused) return;
      this.state.paused = true;
      if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
      this.state.rafId = 0;
    }

    /**
     * Resume scrolling animation
     * @returns {void}
     */
    resume() {
      if (!this.allowAnimation || this._mqlReduced.matches || !this.state.built) return;
      if (this.state.paused) {
        this.state.paused = false;
        this.state.lastTs = null;
        if (this._getScrollSpeed() > 0) {
          this.state.rafId = requestAnimationFrame((ts) => this._step(ts));
        }
      }
    }

    /**
     * Handle resize event (public method for external use)
     * @returns {void}
     */
    onResize() {
      this._onResize();
    }

    /**
     * Rebuild scrolling bar (public method for external use)
     * @returns {void}
     */
    rebuild() {
      this._teardown();
      // Increased timeout for mobile devices with slower connections
      this._waitForImages(this, 1500).then(() => this._build());
    }

    /**
     * Build scrolling bar with clones
     * @returns {void}
     */
    _build() {
      const track = this.elements.track;
      if (!track) return;

      this._removeClones(track);
      this.state.x = 0;
      this.state.lastTs = null;

      const originals = Array.from(track.children);
      if (originals.length <= 1) {
        this.state.built = false;
        track.style.transform = 'translate3d(0, 0, 0)';
        return;
      }

      // 1) Measure base width robustly (right rect - left rect)
      const baseWidth = this._measureBaseWidthOriginals(track, originals.length);
      if (baseWidth <= 0) {
        this.state.built = false;
        track.style.transform = 'translate3d(0, 0, 0)';
        return;
      }

      // 2) Ensure coverage - increased buffer for mobile viewport changes
      const containerWidth = this.clientWidth || track.clientWidth || window.innerWidth || 0;
      const buffer = window.innerWidth < 768 ? 100 : 2;
      const minCoverage = containerWidth + baseWidth + buffer;
      const setsNeeded = Math.max(2, Math.ceil(minCoverage / baseWidth));
      this._appendCloneSets(track, originals, setsNeeded - 1);

      // 3) Start
      this.state.originalItemCount = originals.length;
      this.state.wrapWidth = baseWidth;
      this.state.built = true;

      const speed = this._getScrollSpeed();
      if (!this._mqlReduced.matches && speed > 0) {
        this.state.rafId = requestAnimationFrame((ts) => this._step(ts));
      }
    }

    /**
     * Teardown scrolling bar by cleaning up
     * @returns {void}
     */
    _teardown() {
      if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
      this.state.rafId = 0;
      this.state.lastTs = null;
      this.state.x = 0;

      const track = this.elements.track;
      if (track) {
        this._removeClones(track);
        track.style.transform = 'translate3d(0, 0, 0)';
      }

      this.state.built = false;
    }

    /**
     * Animation step function
     * @param {number} timestamp - The animation frame timestamp
     * @returns {void}
     */
    _step(timestamp) {
      if (this.state.paused || !this.state.built) return;

      const speed = this._getScrollSpeed();
      if (speed <= 0 || this._mqlReduced.matches) {
        this.pause();
        return;
      }

      if (this.state.lastTs == null) this.state.lastTs = timestamp;
      const dt = (timestamp - this.state.lastTs) / 1000;
      this.state.lastTs = timestamp;

      this.state.x += speed * dt;
      if (this.state.x >= this.state.wrapWidth) this.state.x -= this.state.wrapWidth;

      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const snapped = Math.round(this.state.x * dpr) / dpr;

      const { track } = this.elements;
      if (track) track.style.transform = `translate3d(${-snapped}px, 0, 0)`;

      this.state.rafId = requestAnimationFrame((nextTs) => this._step(nextTs));
    }

    /**
     * Get scroll speed based on breakpoint
     * @returns {number} - Scroll speed in pixels per second
     */
    _getScrollSpeed() {
      if (this._mqlReduced.matches) return 0;
      // Use matchMedia only for consistency (avoid window.innerWidth race conditions on mobile)
      if (this._mqlDesktop.matches) return this.scrollSpeedDesktop;
      if (this._mqlTablet.matches) return this.scrollSpeedTablet;
      if (this._mqlMobile.matches) return this.scrollSpeedMobile;
      // Fallback to window.innerWidth only if matchMedia fails
      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      if (w >= 1024) return this.scrollSpeedDesktop;
      if (w >= 768) return this.scrollSpeedTablet;
      return this.scrollSpeedMobile;
    }

    // ---- Coverage helpers ----

    /**
     * Append clone sets to track for infinite scrolling
     * @param {HTMLElement} track - The track element
     * @param {Array<HTMLElement>} originals - Original items to clone
     * @param {number} setsToAdd - Number of sets to add
     * @returns {void}
     */
    _appendCloneSets(track, originals, setsToAdd) {
      if (setsToAdd <= 0) return;
      const frag = document.createDocumentFragment();
      for (let s = 0; s < setsToAdd; s++) {
        for (const el of originals) {
          const clone = el.cloneNode(true);
          clone.classList.add('is-clone');
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
      }
      track.appendChild(frag);
    }

    /**
     * Measure base width of original items
     * @param {HTMLElement} track - The track element
     * @param {number} originalCount - Number of original items
     * @returns {number} - The measured width
     */
    _measureBaseWidthOriginals(track, originalCount) {
      const children = track.children;
      if (!children || children.length < originalCount) return 0;
      const first = children[0];
      const last = children[originalCount - 1];
      if (!first || !last) return 0;

      const r1 = first.getBoundingClientRect();
      const r2 = last.getBoundingClientRect();
      const w = r2.right - r1.left;
      return Number.isFinite(w) && w > 0 ? w : 0;
    }

    /**
     * Remove all cloned elements from track
     * @param {HTMLElement} track - The track element
     * @returns {void}
     */
    _removeClones(track) {
      track.querySelectorAll('.is-clone').forEach((n) => n.remove());
    }

    // Don't block: with timeout; don't use decode for lazy until it enters viewport
    /**
     * Wait for images to load with timeout
     * @param {HTMLElement} scope - The scope element to search for images
     * @param {number} [timeoutMs=1200] - Timeout in milliseconds
     * @returns {Promise<void>}
     */
    _waitForImages(scope, timeoutMs = 1200) {
      const imgs = Array.from(scope.querySelectorAll('img'));
      if (imgs.length === 0) return Promise.resolve();

      const waiters = imgs.map((img) => {
        // if it has dimensions or is complete → done
        if (img.complete && (img.naturalWidth || img.width)) return Promise.resolve();

        // For lazy-loaded images, force load by checking loading attribute
        if (img.loading === 'lazy') {
          img.loading = 'eager';
        }

        // decode can hang for lazy; we use load/error fallback
        return new Promise((res) => {
          const done = () => {
            img.removeEventListener('load', done);
            img.removeEventListener('error', done);
            res();
          };
          img.addEventListener('load', done, { once: true, passive: true });
          img.addEventListener('error', done, { once: true, passive: true });

          // Trigger load if src exists but hasn't started loading
          if (img.src && !img.complete) {
            img.src = img.src;
          }
        });
      });

      const all = Promise.allSettled(waiters).then(() => {});
      const timeout = new Promise((res) => setTimeout(res, timeoutMs));
      // whichever first: don't wait indefinitely
      return Promise.race([all, timeout]);
    }

    /**
     * Debounce helper function
     * @param {Function} fn - Function to debounce
     * @param {number} [delayMs=120] - Delay in milliseconds
     * @returns {Function} - Debounced function
     */
    _debounce(fn, delayMs = 120) {
      let id;
      return (...args) => {
        clearTimeout(id);
        id = setTimeout(() => fn(...args), delayMs);
      };
    }

    // Listeners ----
    /**
     * Handle visibility change event
     * @returns {void}
     */
    _onVisChange() {
      if (document.hidden) this.pause();
      else this.resume();
    }

    /**
     * Handle media query change event
     * @returns {void}
     */
    _onMqlChange() {
      if (this._mqlReduced.matches || this._getScrollSpeed() <= 0) this.pause();
      else this.resume();
    }

    /**
     * Handle intersection observer callback
     * @param {Array<IntersectionObserverEntry>} entries - Intersection observer entries
     * @returns {void}
     */
    _onIntersect(entries) {
      const e = entries[0];
      if (!e) return;
      if (e.isIntersecting) {
        // If it appears for the first time and is not built (was display:none), try rebuild
        // Add small delay on mobile to ensure viewport has settled (iOS Safari issue)
        if (!this.state.built) {
          const isMobile = window.innerWidth < 768;
          if (isMobile) {
            setTimeout(() => this.rebuild(), 150);
          } else {
            this.rebuild();
          }
        } else {
          this.resume();
        }
      } else {
        this.pause();
      }
    }

    /**
     * Auto start fallback function
     * @returns {void}
     */
    _autoStart() {
      // Safety fallback: if it's still not built (e.g. images/layout blocked)
      if (!this.state.built && !this.state.triedAutoStart) {
        this.state.triedAutoStart = true;
        this.rebuild();
      }
    }

    /**
     * Handle mouse enter event (desktop only)
     * @returns {void}
     */
    _onMouseEnter() {
      // Only pause on desktop (width > 1023px)
      if (this.pauseOnHoverDesktop && this._mqlDesktop.matches) {
        this.pause();
      }
    }

    /**
     * Handle mouse leave event (desktop only)
     * @returns {void}
     */
    _onMouseLeave() {
      // Only resume on desktop (width > 1023px)
      if (this.pauseOnHoverDesktop && this._mqlDesktop.matches) {
        this.resume();
      }
    }
  }

  customElements.define('wlsn-scrolling-bar', WLSNScrollingBar);
}
